import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Button, Typography, colors } from '@leapeasy/ui-kit';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { PolicyCancelLogTable } from 'components/Tables/PolicyCancelLogTable';
import { getPolicyCancelLogsAction } from 'store/actions/propertyActions';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { withBreadcrumbParents } from 'utils/breadcrumbNavigation';
import { toPlainCancelLogs } from 'utils/policyCancelLog';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  arePolicyCancelLogDateRangeFiltersEqual,
  EMPTY_POLICY_CANCEL_LOG_DATE_RANGE_FILTER,
  hasActivePolicyCancelLogDateRangeFilter,
  normalizePolicyCancelLogDateRangeFilter,
} from 'utils/policyCancelLogDateRangeFilter';
import {
  buildPolicyCancelLogRequestFilter,
  DEFAULT_POLICY_CANCEL_LOG_EXTRA_FILTERS,
  parsePolicyCancelLogExtraFilters,
  serializePolicyCancelLogExtraFilters,
} from 'utils/policyCancelLogListQueryFilters';

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const PolicyCancelLog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cancelLogsRaw = useSelector((state) => state.getIn(['property', 'policy-cancel-logs']));

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Policy Cancel Log..',
    },
    defaultRowsPerPage: 10,
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_POLICY_CANCEL_LOG_EXTRA_FILTERS,
    parseExtraFilters: parsePolicyCancelLogExtraFilters,
    serializeExtraFilters: serializePolicyCancelLogExtraFilters,
  });

  const [selectedRoles, setSelectedRoles] = useState(() =>
    Array.isArray(extraFilters?.selectedRoles) ? extraFilters.selectedRoles : [],
  );
  const [selectedRolesDraft, setSelectedRolesDraft] = useState(() =>
    Array.isArray(extraFilters?.selectedRoles) ? extraFilters.selectedRoles : [],
  );
  const [selectedCancellationDate, setSelectedCancellationDate] = useState(() =>
    normalizePolicyCancelLogDateRangeFilter(extraFilters?.selectedCancellationDate),
  );
  const [selectedCancellationDateDraft, setSelectedCancellationDateDraft] = useState(() =>
    normalizePolicyCancelLogDateRangeFilter(extraFilters?.selectedCancellationDate),
  );

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextRoles = Array.isArray(extraFilters.selectedRoles) ? extraFilters.selectedRoles : [];
    const nextCancellationDate = normalizePolicyCancelLogDateRangeFilter(
      extraFilters.selectedCancellationDate,
    );
    setSelectedRoles((prev) => (areArraysEqual(prev, nextRoles) ? prev : nextRoles));
    setSelectedRolesDraft((prev) => (areArraysEqual(prev, nextRoles) ? prev : nextRoles));
    setSelectedCancellationDate((prev) =>
      arePolicyCancelLogDateRangeFiltersEqual(prev, nextCancellationDate)
        ? prev
        : nextCancellationDate,
    );
    setSelectedCancellationDateDraft((prev) =>
      arePolicyCancelLogDateRangeFiltersEqual(prev, nextCancellationDate)
        ? prev
        : nextCancellationDate,
    );
  }, [extraFilters]);

  const handleDispatch = useCallback(() => {
    dispatch(
      getPolicyCancelLogsAction({
        filter: buildPolicyCancelLogRequestFilter({
          filter,
          selectedRoles,
          selectedCancellationDate,
        }),
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions,
      }),
    );
  }, [
    dispatch,
    filter,
    selectedRoles,
    selectedCancellationDate,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
  ]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const cancelLogsState = useMemo(() => toPlainCancelLogs(cancelLogsRaw), [cancelLogsRaw]);
  const logs = cancelLogsState?.data ?? [];
  const cancelLogTotal = Number(cancelLogsState?.total) || 0;
  const cancelLogError = cancelLogsState?.cancelLogsError;
  const isLoading = cancelLogsState == null || Boolean(cancelLogsState?.isLoading);

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: cancelLogTotal,
    isLoading,
  });

  const resetFilters = useCallback(() => {
    setSelectedRoles([]);
    setSelectedRolesDraft([]);
    setSelectedCancellationDate({ ...EMPTY_POLICY_CANCEL_LOG_DATE_RANGE_FILTER });
    setSelectedCancellationDateDraft({ ...EMPTY_POLICY_CANCEL_LOG_DATE_RANGE_FILTER });
    setExtraFilters({
      selectedRoles: [],
      selectedCancellationDate: { ...EMPTY_POLICY_CANCEL_LOG_DATE_RANGE_FILTER },
    });
    setFilter((prev) => ({
      ...prev,
      searchText: '',
    }));
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setFilter, setPagination]);

  const applyFilters = useCallback(() => {
    setSelectedRoles(selectedRolesDraft);
    setSelectedCancellationDate(selectedCancellationDateDraft);
    setExtraFilters({
      selectedRoles: selectedRolesDraft,
      selectedCancellationDate: selectedCancellationDateDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [selectedRolesDraft, selectedCancellationDateDraft, setExtraFilters, setPagination]);

  const dropFilterActiveCount =
    selectedRoles.length +
    (hasActivePolicyCancelLogDateRangeFilter(selectedCancellationDate) ? 1 : 0);

  const hasSearch = Boolean(`${filter?.searchText || ''}`.trim());
  const hasAppliedFilters = hasSearch || dropFilterActiveCount > 0;

  const onViewApplication = useCallback(
    (application) => {
      if (!application?.app_id) {
        dispatch(failedNotif('Application detail ID is missing.'));
        return;
      }

      dispatch(
        openDetails({
          type: 'application',
          data: application,
        }),
      );

      navigate(`/property/applications/${application.app_id}/detail`, {
        state: withBreadcrumbParents([{ name: 'Policy Cancel Log', link: '/cancellation-log' }], {
          fromCancellationLog: true,
        }),
      });
    },
    [dispatch, navigate],
  );

  return (
    <DashboardLayoutContainer>
      <PageHeader>
        <Typography variant="h5" style={{ color: colors.black[900], fontWeight: 600 }}>
          Policy Cancel Log
        </Typography>
        <Typography variant="body2" style={{ color: colors.neutral[700], marginTop: 8 }}>
          This table shows the list of all policy cancellation activities.
        </Typography>
      </PageHeader>
      <TableWrap>
        {cancelLogError && !isLoading ? (
          <ErrorWrap>
            <GlobalUXState variant="apiError" compact description={cancelLogError} />
            <Button variant="primary" size="medium" onClick={handleDispatch}>
              Retry
            </Button>
          </ErrorWrap>
        ) : null}
        <PolicyCancelLogTable
          cancelLogs={logs}
          loading={isLoading}
          onViewApplication={onViewApplication}
          filter={filter}
          onChangeFilter={setFilter}
          sortOptions={sortOptions}
          onChangeSort={setSortOptions}
          pagination={{
            ...pagination,
            totalItems: cancelLogTotal,
            totalPages: Math.ceil(cancelLogTotal / pagination.rowsPerPage) || 0,
          }}
          onChangePagination={setPagination}
          selectedRolesDraft={selectedRolesDraft}
          onSelectedRolesDraftChange={setSelectedRolesDraft}
          selectedCancellationDateDraft={selectedCancellationDateDraft}
          onSelectedCancellationDateDraftChange={setSelectedCancellationDateDraft}
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          dropFilterActiveCount={dropFilterActiveCount}
          hasAppliedFilters={hasAppliedFilters}
        />
      </TableWrap>
    </DashboardLayoutContainer>
  );
};

const PageHeader = styled(Box)`
  margin-bottom: 24px;
`;

const TableWrap = styled(Box)`
  position: relative;
  min-height: 200px;
`;

const ErrorWrap = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;
